import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { useTranslation } from "react-i18next";
import {
  formatInternalNoteHtml,
  shouldCollapseInternalNote,
} from "../../../utils/whatsappTextFormat";
import { stripOperatorNamePrefix } from "../../../utils/operatorNamePrefix";

export const INTERNAL_NOTE_PREVIEW_MAX_HEIGHT_PX = 148;

export const internalNoteContentSx = {
  color: "text.primary",
  textAlign: "left",
  lineHeight: 1.55,
  fontSize: "0.875rem",
  wordBreak: "break-word",
  "& strong, & b": { fontWeight: 700 },
  "& em, & i": { fontStyle: "italic" },
  "& s, & strike, & del": { textDecoration: "line-through" },
  "& code": {
    fontFamily: "monospace",
    fontSize: "0.9em",
    px: 0.5,
    py: 0.15,
    borderRadius: 0.5,
    bgcolor: "action.hover",
  },
  "& ul, & ol": { pl: 2.5, my: 0.75 },
  "& li": { mb: 0.35 },
  "& p": { my: 0.5 },
  "& p:first-of-type": { mt: 0 },
  "& p:last-of-type": { mb: 0 },
  "& a": { color: "primary.main", wordBreak: "break-all" },
};

const InternalNoteContent = ({ text, authorLabel, authorNamesForStrip, previewFadeColor }) => {
  const { t } = useTranslation("chat");
  const [dialogOpen, setDialogOpen] = useState(false);

  const displayText = useMemo(() => {
    const names = authorNamesForStrip?.length
      ? authorNamesForStrip
      : authorLabel
        ? [authorLabel]
        : [];
    return stripOperatorNamePrefix(text, names);
  }, [text, authorLabel, authorNamesForStrip]);

  const formattedHtml = useMemo(() => formatInternalNoteHtml(displayText), [displayText]);
  const collapsible = useMemo(() => shouldCollapseInternalNote(displayText), [displayText]);

  if (!formattedHtml) return null;

  return (
    <>
      <Box sx={{ position: "relative" }}>
        <Box
          sx={{
            ...internalNoteContentSx,
            maxHeight: collapsible ? INTERNAL_NOTE_PREVIEW_MAX_HEIGHT_PX : "none",
            overflow: collapsible ? "hidden" : "visible",
          }}
          dangerouslySetInnerHTML={{ __html: formattedHtml }}
        />
        {collapsible && (
          <Box
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: 56,
              background: previewFadeColor
                ? `linear-gradient(to bottom, transparent, ${previewFadeColor})`
                : (theme) =>
                    `linear-gradient(to bottom, transparent, ${theme.palette.background.paper})`,
              pointerEvents: "none",
            }}
          />
        )}
      </Box>

      {collapsible && (
        <Button
          size="small"
          startIcon={<UnfoldMoreIcon sx={{ fontSize: 16 }} />}
          onClick={() => setDialogOpen(true)}
          sx={{
            mt: 0.75,
            px: 0,
            minWidth: 0,
            textTransform: "none",
            fontSize: "0.78rem",
            fontWeight: 600,
          }}
        >
          {t("messages.noteExpandFull", "Ver anotação completa")}
        </Button>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ pr: 6 }}>
          {authorLabel
            ? t("messages.noteFullTitleWithAuthor", "Anotação — {{author}}", { author: authorLabel })
            : t("messages.noteFullTitle", "Anotação interna")}
          <IconButton
            aria-label={t("messages.noteCloseFull", "Fechar")}
            onClick={() => setDialogOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={internalNoteContentSx} dangerouslySetInnerHTML={{ __html: formattedHtml }} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InternalNoteContent;
